/**
 * useAdminStats Hook
 * Fetches real admin dashboard statistics from the API
 */

import { useState, useEffect, useCallback } from 'react';
import { APIError } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://onlyjalen-api.jalen1wa.workers.dev';

interface RecentOffer {
  id: string;
  amount: number;
  status: string;
  created_at: string;
  product_name: string;
  buyer_name: string;
}

interface RecentSubscriber {
  id: string;
  display_name: string;
  email: string;
  created_at: string;
  plan_type?: string;
  subscription_status?: string;
}

export interface AdminStats {
  // Core counts
  unread_messages: number;
  pending_offers: number;
  awaiting_payment: number;
  active_products: number;
  sold_products: number;
  total_subscribers: number;
  active_subscribers: number;
  active_subscriptions: number;
  pending_subscriptions: number;
  total_posts: number;
  total_stories: number;

  // Revenue
  total_revenue: number;
  total_sales: number;
  subscription_revenue: number;
  subscription_count: number;

  // 24h activity
  new_users_24h: number;
  new_offers_24h: number;
  new_messages_24h: number;
  new_subscriptions_24h: number;

  // Recent activity feeds
  recent_offers: RecentOffer[];
  recent_subscribers: RecentSubscriber[];
}

interface UseAdminStatsReturn {
  stats: AdminStats | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

async function apiRequest<T>(endpoint: string): Promise<T> {
  const token = localStorage.getItem('onlyjalen_access_token');

  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new APIError(data.error || 'API request failed', response.status);
  }

  return response.json();
}

export function useAdminStats(): UseAdminStatsReturn {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest<{ success: boolean; stats: AdminStats }>('/api/admin/stats');
      setStats(data.stats);
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to load stats';
      setError(message);
      console.error('useAdminStats error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Refresh stats every 30 seconds
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [refresh]);

  return { stats, loading, error, refresh };
}

export default useAdminStats;
